import REST_CLIENT from '../utils/restClient';
import getClientConfig from '../utils/getClientConfig';
import IAM_USERS_CONTROLLER from './iamUsers';
import {
    DIRECT_SUBMISSION_ROLES,
    DIRECT_SUBMISSION_PERMISSIONS,
    DEFAULT_CONDITION,
    WILDCARD,
} from '../components/directSubmissionPermissions/constants';
import LANGUAGES_CONTROLLER from './languages';
import Logger, { LOG_LEVELS } from '../utils/logger';
import { toastActions } from '../components/inAppMessages/toastActions';
import {
    parseDsCondition,
    buildGrantUpdateBody,
    getUserDsGrants,
} from '../utils/dsGrantReconcile';

export const DS_PERMISSIONS_TOAST_SCOPE = 'directSubmissionPermissions';

export const MODULE_NAME = 'DIRECT_SUBMISSION_PERMISSIONS_CONTROLLER';

const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
    return `${MODULE_NAME}.${name}`;
}

const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_FAIL = namespaced('LOAD_FAIL');
const LOAD_COMPANY_DETAILS_OK = namespaced('LOAD_COMPANY_DETAILS_OK');
const LOAD_COMPANY_DETAILS_FAIL = namespaced('LOAD_COMPANY_DETAILS_FAIL');
const LOAD_COMPANY_VAT_NUMBERS_OK = namespaced('LOAD_COMPANY_VAT_NUMBERS_OK');
const APPLY = namespaced('APPLY');
const APPLY_OK = namespaced('APPLY_OK');
const APPLY_FAIL = namespaced('APPLY_FAIL');

export const ACTION_TYPES = {
    LOAD,
    LOAD_OK,
    LOAD_FAIL,
    LOAD_COMPANY_DETAILS_OK,
    LOAD_COMPANY_DETAILS_FAIL,
    LOAD_COMPANY_VAT_NUMBERS_OK,
    APPLY,
    APPLY_OK,
    APPLY_FAIL,
};

const initialState = {
    data: undefined,
    loading: false,
    error: null,
    applying: false,
    applyError: null,
};

const ROLE_META = {
    initiator: { permission: DIRECT_SUBMISSION_PERMISSIONS.INITIATE },
    approver: { permission: DIRECT_SUBMISSION_PERMISSIONS.APPROVE },
};

const DS_ROLE_NAMES = [DIRECT_SUBMISSION_ROLES.INITIATOR, DIRECT_SUBMISSION_ROLES.APPROVER];

const getBaseRoleDisplayName = (user) => {
    const entries = user?.userAccess || [];
    const base = entries.find(a => a?.role?.displayName && !DS_ROLE_NAMES.includes(a.role.displayName));
    return base?.role?.displayName || entries[0]?.role?.displayName || '-';
};

const extractDsRolesFromUserAccess = (user) => {
    const entries = user?.userAccess || [];
    const hasRole = (roleName) => entries.some(a => a?.role?.displayName === roleName);
    return {
        initiator: hasRole(DIRECT_SUBMISSION_ROLES.INITIATOR),
        approver: hasRole(DIRECT_SUBMISSION_ROLES.APPROVER),
    };
};

const extractDsScopeFromUserAccess = (user) => {
    const entries = user?.userAccess || [];
    const dsEntry = entries.find(a =>
        DS_ROLE_NAMES.includes(a?.role?.displayName) && a?.rules?.[0]?.condition
    );
    return dsEntry ? parseDsCondition(dsEntry.rules[0].condition) : null;
};

const buildCompanyClause = (companyId, countries) => {
    const codes = Object.keys(countries || {}).filter(code => countries[code]);
    if (codes.length === 0) {
        return null;
    }
    if (codes.length === Object.keys(countries).length) {
        return `(companyId EQ '${companyId}' AND countryCode EQ '${WILDCARD}')`;
    }
    return codes
        .map(code => `(companyId EQ '${companyId}' AND countryCode EQ '${code}')`)
        .join(' OR ');
};

const buildConditionFromSelections = (selections) => {
    const clauses = Object.entries(selections || {})
        .map(([companyId, value]) => buildCompanyClause(companyId, value.countries))
        .filter(Boolean);
    return clauses.length ? clauses.join(' OR ') : DEFAULT_CONDITION;
};

const getDsGrantTemplates = async (iamSystemId) => {
    const response = await REST_CLIENT.getIamGrants(iamSystemId);

    const toTemplate = (grant) => grant && {
        sourceGrantId: grant.id,
        roleId: grant.role?.identifier,
        systemId: grant.system?.identifier,
    };

    return {
        initiator: toTemplate(response?.items?.find(g => g.displayName === DIRECT_SUBMISSION_ROLES.INITIATOR)),
        approver: toTemplate(response?.items?.find(g => g.displayName === DIRECT_SUBMISSION_ROLES.APPROVER)),
    };
};

const buildCustomGrantBody = (iamTenantId, template, role, iamUserId, condition) => {
    const { permission } = ROLE_META[role];
    return {
        type: 'custom',
        tenant: { identifier: iamTenantId },
        system: { identifier: template.systemId },
        role: { identifier: template.roleId },
        source: { identifier: template.sourceGrantId },
        displayName: `DS-${role}-${iamUserId}`,
        description: `Direct Submission ${role} grant for ${iamUserId}`,
        rules: [{ permissions: [permission], condition }],
        resources: [{ resourceNamespace: 'directsubmission', permissions: [permission] }],
    };
};

/** Bounds concurrent grant fetches while hydrating scope on load. */
const DS_SCOPE_HYDRATION_CONCURRENCY = 8;

const needsScopeHydration = (user) => {
    const roles = user.directSubmissionRoles;
    return !user.dsScope && !!user.iamUserId && (roles.initiator || roles.approver);
};

const fetchDsScope = async (iamTenantId, iamUserId) => {
    const grants = await getUserDsGrants(iamTenantId, iamUserId);
    const condition = (grants.initiator || grants.approver)?.rules?.[0]?.condition;
    return condition ? parseDsCondition(condition) : null;
};

// The IAM users list only carries roles in userAccess, not grant conditions. For users with a
// DS role we fetch their grant (in bounded batches) to surface the restricted scope in the table.
const hydrateDsScopes = async (iamTenantId, users) => {
    const targets = users.filter(needsScopeHydration);
    if (!targets.length) {
        return users;
    }

    const scopeByUserId = {};
    for (let i = 0; i < targets.length; i += DS_SCOPE_HYDRATION_CONCURRENCY) {
        const batch = targets.slice(i, i + DS_SCOPE_HYDRATION_CONCURRENCY);
        await Promise.all(batch.map(async (user) => {
            scopeByUserId[user.id] = await fetchDsScope(iamTenantId, user.iamUserId).catch(error => {
                log.warn(`Failed to load Direct Submission scope for user ${user.iamUserId}`, error);
                return null;
            });
        }));
    }

    return users.map(user =>
        scopeByUserId[user.id] ? { ...user, dsScope: scopeByUserId[user.id] } : user
    );
};

const resolveCondition = (scopeChanged, sessionSelections, existingGrants) => {
    if (scopeChanged) {
        return buildConditionFromSelections(sessionSelections);
    }
    const existing = existingGrants.initiator || existingGrants.approver;
    return existing?.rules?.[0]?.condition || DEFAULT_CONDITION;
};

const createAndAssignGrant = async (ctx, role) => {
    const template = ctx.templates[role];
    if (!template?.sourceGrantId) {
        throw new Error(`Grant template not found for role: ${role}`);
    }
    const body = buildCustomGrantBody(ctx.iamTenantId, template, role, ctx.iamUserId, ctx.condition);
    const created = await REST_CLIENT.createIamGrant(body);
    await REST_CLIENT.assignGrantToIamUser(ctx.iamTenantId, ctx.iamUserId, created.id);
};

const applyRole = async (ctx, role) => {
    const grant = ctx.existingGrants[role];

    if (ctx.want[role]) {
        if (!grant) {
            await createAndAssignGrant(ctx, role);
        } else if (ctx.scopeChanged) {
            await REST_CLIENT.updateIamGrant(grant.id, buildGrantUpdateBody(grant, ctx.condition));
        }
        return;
    }

    if (grant) {
        await REST_CLIENT.removeGrantFromIamUser(ctx.iamTenantId, ctx.iamUserId, grant.id);
    }
};

const computeWant = (had, changes) => ({
    initiator: changes?.initiator ?? had.initiator,
    approver: changes?.approver ?? had.approver,
});

const applyUserChanges = async ({ iamTenantId, templates, user, changes, sessionSelections }) => {
    const iamUserId = user.iamUserId;
    if (!iamUserId) {
        return;
    }

    const want = computeWant(user.directSubmissionRoles, changes);
    const scopeChanged = sessionSelections !== undefined;
    const existingGrants = await getUserDsGrants(iamTenantId, iamUserId);
    const condition = resolveCondition(scopeChanged, sessionSelections, existingGrants);

    const ctx = { iamTenantId, iamUserId, want, condition, scopeChanged, templates, existingGrants };
    await applyRole(ctx, 'initiator');
    await applyRole(ctx, 'approver');
};

const getAffectedUserIds = (permissionChanges, scopeAssignments) =>
    [...new Set([
        ...Object.keys(permissionChanges || {}),
        ...Object.keys(scopeAssignments || {}),
    ])];

const computeUpdatedUsers = (users, permissionChanges, scopeAssignments) => {
    return users.map(user => {
        const changes = permissionChanges[user.id];
        const selections = scopeAssignments[user.id];
        if (!changes && !selections) {
            return user;
        }
        const next = { ...user, directSubmissionRoles: computeWant(user.directSubmissionRoles, changes) };
        if (selections) {
            next.appliedScope = selections;
        }
        return next;
    });
};

export const directSubmissionPermissionsReducer = (state = initialState, action) => {
    switch (action.type) {
        case LOAD:
            return { ...state, loading: true, error: null, companyVatNumbers: {} };
        case LOAD_OK:
            return { ...state, data: action.response, loading: false, error: null };
        case LOAD_FAIL:
            return { ...state, data: undefined, loading: false, error: action.error };
        case LOAD_COMPANY_DETAILS_OK: {
            const allCompanies = action.response;
            const companiesLookup = allCompanies ?
                Object.fromEntries(allCompanies.map((company) =>
                    [company.id, `${company.code} - ${company.name}`]
                )) :
                {};
            return { ...state, allCompanies, companiesLookup };
        }
        case LOAD_COMPANY_DETAILS_FAIL:
            return { ...state, allCompanies: undefined };
        case LOAD_COMPANY_VAT_NUMBERS_OK: {
            const existing = state.companyVatNumbers || {};
            return {
                ...state,
                companyVatNumbers: { ...existing, ...action.response }
            };
        }
        case APPLY:
            return { ...state, applying: true, applyError: null };
        case APPLY_OK:
            return { ...state, data: action.response, applying: false, applyError: null };
        case APPLY_FAIL:
            return { ...state, applying: false, applyError: action.error };
        default:
            return state;
    }
};

export const API = {
    doLoad: (tenantId, iamTenantId, errorHandler) => {
        return async (dispatch) => {
            dispatch({ type: LOAD });
            try {
                const users = await dispatch(IAM_USERS_CONTROLLER.doEnsureLoaded(iamTenantId));

                const companyGrants = await REST_CLIENT.getCompanyPermissions(tenantId);

                if (!companyGrants?.userCompanyIds) {
                    throw new Error('Invalid company grants response');
                }

                const baseUsers = users.map(user => ({
                    id: user.userName,
                    iamUserId: user.id,
                    firstName: user.name.givenName,
                    lastName: user.name.familyName,
                    role: getBaseRoleDisplayName(user),
                    companyGrants: companyGrants.userCompanyIds?.[user.userName] || [],
                    directSubmissionRoles: extractDsRolesFromUserAccess(user),
                    dsScope: extractDsScopeFromUserAccess(user),
                }));

                const response = await hydrateDsScopes(iamTenantId, baseUsers);

                dispatch({ type: LOAD_OK, response });
                dispatch(API.doLoadCompanies());
            } catch (error) {
                dispatch({ type: LOAD_FAIL, error });
                errorHandler();
            }
        };
    },

    doApplyChanges: (permissionChanges, scopeAssignments) => {
        return async (dispatch, getState) => {
            dispatch({ type: APPLY });
            try {
                const state = getState();
                const iamTenantId = state.authentication?.data?.iamData?.tenantId;
                const { iamNamespace } = getClientConfig();

                const iamSystemResponse = await REST_CLIENT.getIamSystemId(iamNamespace);
                const iamSystemId = iamSystemResponse.items[0].id;
                const templates = await getDsGrantTemplates(iamSystemId);

                const users = state.directSubmissionPermissions?.data || [];
                const affectedIds = getAffectedUserIds(permissionChanges, scopeAssignments);

                await Promise.all(affectedIds.map(userId => {
                    const user = users.find(u => u.id === userId);
                    if (!user) {
                        return Promise.resolve();
                    }
                    return applyUserChanges({
                        iamTenantId,
                        templates,
                        user,
                        changes: permissionChanges[userId],
                        sessionSelections: scopeAssignments[userId],
                    });
                }));

                const updatedUsers = computeUpdatedUsers(users, permissionChanges, scopeAssignments);
                dispatch({ type: APPLY_OK, response: updatedUsers });

                const languages = getState().languages;
                dispatch(toastActions.addToast(
                    'ds-permissions-apply',
                    LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_ApplySuccessTitle'),
                    LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_ApplySuccess'),
                    'success',
                    DS_PERMISSIONS_TOAST_SCOPE
                ));
            } catch (error) {
                dispatch({ type: APPLY_FAIL, error });
                throw error;
            }
        };
    },

    doGetUserDsScope: (iamUserId) => {
        return async (dispatch, getState) => {
            const state = getState();
            const iamTenantId = state.authentication?.data?.iamData?.tenantId;
            const grants = await getUserDsGrants(iamTenantId, iamUserId);
            const grant = grants.initiator || grants.approver;
            return grant?.rules?.[0]?.condition
                ? parseDsCondition(grant.rules[0].condition)
                : { isDefault: true, pairs: [] };
        };
    },

    doLoadCompanies: () => {
        return async (dispatch) => {
            try {
                const response = await REST_CLIENT.getCompaniesSummaryV1();
                dispatch({ type: LOAD_COMPANY_DETAILS_OK, response });
            } catch {
                dispatch({ type: LOAD_COMPANY_DETAILS_FAIL });
            }
        };
    },

    doLoadCompanyVatNumbers: (companyIds) => {
        return async (dispatch) => {
            const results = await Promise.allSettled(
                companyIds.map(id => REST_CLIENT.getCompanyDetailsById(id))
            );

            const vatNumbersByCompany = {};
            results.forEach((result, index) => {
                if (result.status !== 'fulfilled') return;

                const company = result.value;
                const countries = (company.vatNumbers || [])
                    .map(vn => vn.vatnumberCountry)
                    .filter(Boolean);

                vatNumbersByCompany[companyIds[index]] = {
                    countries: [...new Set(countries)].sort((a, b) => a.localeCompare(b)),
                    vatNumbers: (company.vatNumbers || []).map(vn => ({
                        country: vn.vatnumberCountry,
                        vatNumber: vn.vatnumber
                    }))
                };
            });

            dispatch({ type: LOAD_COMPANY_VAT_NUMBERS_OK, response: vatNumbersByCompany });
            return vatNumbersByCompany;
        };
    },
};

export default API;
